import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Crown, Radio } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | pending | failed
  const [countdown, setCountdown] = useState(4);
  const sessionId = params.get("session_id");
  const provider = params.get("provider");
  const paypalOrderId = params.get("token"); // PayPal appends ?token=<orderId>&PayerID=... on return

  useEffect(() => {
    let tries = 0;
    const finalize = async () => {
      if (sessionId) {
        const poll = async () => {
          try {
            const { data } = await endpoints.stripeStatus(sessionId);
            if (data.payment_status === "paid") { await refresh(); setState("success"); return; }
            if (data.status === "expired") { setState("failed"); return; }
          } catch {}
          if (tries++ < 8) setTimeout(poll, 2000); else setState("pending");
        };
        poll();
      } else if (provider === "paypal" && paypalOrderId) {
        // Capture the approved PayPal order to actually collect payment & activate the plan.
        try {
          const { data } = await endpoints.paypalCapture(paypalOrderId);
          if (data.status === "paid") { await refresh(); setState("success"); return; }
          setState("pending");
        } catch (e) {
          setState(e?.response?.status === 402 ? "failed" : "pending");
        }
      } else if (provider) {
        await refresh();
        setState("success");
      } else {
        await refresh();
        setState("success");
      }
    };
    finalize();
  }, [sessionId, provider, paypalOrderId]);

  // After activation: Thank You → auto-redirect to Live Radio so the customer presses Play.
  useEffect(() => {
    if (state !== "success") return;
    if (countdown <= 0) { nav("/radio"); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [state, countdown, nav]);

  return (
    <div className="px-4 max-w-md mx-auto py-24 text-center" data-testid="payment-success-page">
      <div className="rounded-2xl glass border border-[#273042] p-10">
        {state === "checking" && <><Loader2 className="w-14 h-14 text-[#E11D2A] animate-spin mx-auto" /><h1 className="text-2xl font-bold text-white mt-5">Confirming your payment…</h1><p className="text-slate-400 mt-2 text-sm">Hang tight, this only takes a moment.</p></>}
        {state === "success" && <><span className="w-16 h-16 rounded-full bg-[#22C55E]/15 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-[#22C55E]" /></span><h1 className="text-2xl font-extrabold text-white mt-5">Thank you! You're all set 🎉</h1><p className="text-slate-400 mt-2 flex items-center justify-center gap-1.5"><Crown className="w-4 h-4 text-[#2563EB]" /> Your membership is active.</p><p className="text-slate-500 text-xs mt-2">A PDF receipt has been sent to your WhatsApp.</p><p className="text-slate-300 text-sm mt-4 flex items-center justify-center gap-1.5"><Radio className="w-4 h-4 text-[#E11D2A]" /> Taking you to Live Radio in {countdown}s…</p><div className="flex gap-3 justify-center mt-6"><Link to="/radio" data-testid="success-radio-btn" className="h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold flex items-center gap-1.5"><Radio className="w-4 h-4" /> Go to Live Radio</Link><Link to="/vod" className="h-11 px-6 rounded-full bg-white/5 border border-[#273042] text-white font-semibold flex items-center">Watch shows</Link></div></>}
        {state === "pending" && <><Loader2 className="w-14 h-14 text-[#2563EB] animate-spin mx-auto" /><h1 className="text-2xl font-bold text-white mt-5">Payment pending</h1><p className="text-slate-400 mt-2 text-sm">We'll activate your plan as soon as it clears. Check My Account shortly.</p><Link to="/profile" className="mt-6 inline-flex h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold items-center">Go to My Account</Link></>}
        {state === "failed" && <><span className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto"><XCircle className="w-9 h-9 text-red-400" /></span><h1 className="text-2xl font-bold text-white mt-5">Payment not completed</h1><p className="text-slate-400 mt-2 text-sm">No charge was made. You can try again anytime.</p><Link to="/pricing" className="mt-6 inline-flex h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold items-center">Back to plans</Link></>}
      </div>
    </div>
  );
}
